import React, { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Paper,
  Tabs,
  Tab,
  Chip,




  Alert,

  Grid,
} from "@mui/material";
import {
  ExpandMore,
  Add,
  Delete,
  Edit,
  ArrowUpward,
  ArrowDownward,
  Preview,
  Code,
  Save,
  Upload,
  Download,
} from "@mui/icons-material";
import {
  Form,
  Question,
  QuestionType,
  Option,


  Condition,
} from "../../api/types/forms";
import { v4 as uuidv4 } from "uuid";
import DynamicFormRenderer from "./DynamicFormRenderer";
import { FormAnswers } from "../../api/types/forms";

interface FormCreatorProps {
  initialForm?: Form | null;
  onSave: (form: Form) => void;
  onCancel?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ width: "100%" }}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const FormCreator: React.FC<FormCreatorProps> = ({ initialForm, onSave, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<Form>(() => {
    if (initialForm) {
      return { ...initialForm };
    }
    return {
      form_id: uuidv4(),
      organizationId: '',
      connector: null,
      title: "",
      description: null,
      questions: [],
    };
  });

  const [activeTab, setActiveTab] = useState(0);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<number | false>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewAnswers, setPreviewAnswers] = useState<FormAnswers>([]);

  // Question editor state
  const [_editingQuestion, setEditingQuestion] = useState<Question | null>(null);

  useEffect(() => {
    if (initialForm) {
      setForm({ ...initialForm });
    }
  }, [initialForm]);

  const validateForm = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!form.title || form.title.trim() === "") {
      newErrors.title = "El título es requerido";
    }

    if (!form.form_id || form.form_id.trim() === "") {
      newErrors.form_id = "El ID del formulario es requerido";
    }

    // Validate questions
    form.questions.forEach((question, index) => {
      if (!question.text || question.text.trim() === "") {
        newErrors[`question_${index}_text`] = "El texto de la pregunta es requerido";
      }
      if (!question.id || question.id.trim() === "") {
        newErrors[`question_${index}_id`] = "El ID de la pregunta es requerido";
      }

      // Check for duplicate question IDs
      const duplicateIds = form.questions.filter((q, i) => q.id === question.id && i !== index);
      if (duplicateIds.length > 0) {
        newErrors[`question_${index}_id`] = "El ID de la pregunta debe ser único";
      }

      // Validate options for select/multiselect/radio
      if (["select", "multiselect", "radio"].includes(question.type)) {
        if (!question.options || question.options.length === 0) {
          newErrors[`question_${index}_options`] =
            "Las preguntas de selección requieren al menos una opción";
        } else {
          // Check for duplicate option values
          const optionValues = question.options.map((opt) => opt.value);
          const duplicateValues = optionValues.filter((val, i) => optionValues.indexOf(val) !== i);
          if (duplicateValues.length > 0) {
            newErrors[`question_${index}_options`] = "Los valores de las opciones deben ser únicos";
          }
        }
      }

      // Validate display logic references
      if (question.display_logic.condition_type === "if" && question.display_logic.conditions) {
        question.display_logic.conditions.forEach((conditionGroup, groupIndex) => {
          conditionGroup.forEach((condition, condIndex) => {
            const sourceExists = form.questions.some((q) => q.id === condition.source_question_id);
            if (!sourceExists) {
              newErrors[`question_${index}_display_logic_${groupIndex}_${condIndex}`] =
                `La pregunta fuente "${condition.source_question_id}" no existe`;
            }
          });
        });
      }

      if (question.display_logic.condition_type === "for_each") {
        if (!question.display_logic.source_multiselect_id) {
          newErrors[`question_${index}_for_each_source`] =
            "Se requiere una pregunta fuente multiselect";
        } else {
          // The reference may be a stringId (editor format) or a UUID (legacy/backend format)
          const sourceId = question.display_logic.source_multiselect_id;
          const sourceExists = form.questions.some(
            (q) => (q.id === sourceId || q.uuid === sourceId) && q.type === "multiselect"
          );
          if (!sourceExists) {
            newErrors[`question_${index}_for_each_source`] =
              "La pregunta fuente debe ser de tipo multiselect y existir";
          }
        }
      }
    });

    setErrors(newErrors);
    return newErrors;
  }, [form]);

  const handleSave = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length === 0) {
      onSave(form);
      return;
    }

    setActiveTab(0);
    const firstQuestionWithError = Object.keys(validationErrors).reduce<number | null>(
      (min, key) => {
        const match = /^question_(\d+)_/.exec(key);
        if (!match) return min;
        const index = parseInt(match[1], 10);
        return min === null || index < min ? index : min;
      },
      null
    );
    if (firstQuestionWithError !== null) {
      setExpandedQuestion(firstQuestionWithError);
    }
    enqueueSnackbar("El formulario tiene errores de validación. Revise los campos marcados.", {
      variant: "error",
    });
  };

  const handleAddQuestion = () => {
    const newQuestion: Question = {
      id: uuidv4(),
      uuid: uuidv4(),
      text: "",
      help_text: null,
      type: "text" as any,
      mandatory: false,
      placeholder: null,
      default_value: null,
      validation: null,
      display_logic: {
        condition_type: "always",
      },
      attachments: [],
    };
    setForm({
      ...form,
      questions: [...form.questions, newQuestion],
    });
    setEditingQuestionIndex(form.questions.length);
    setExpandedQuestion(form.questions.length);
  };

  const handleDeleteQuestion = (index: number) => {
    const newQuestions = form.questions.filter((_, i) => i !== index);
    setForm({
      ...form,
      questions: newQuestions,
    });
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null);
      setEditingQuestion(null);
    }
  };

  const handleMoveQuestion = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === form.questions.length - 1)
    ) {
      return;
    }

    const newQuestions = [...form.questions];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newQuestions[index], newQuestions[targetIndex]] = [
      newQuestions[targetIndex],
      newQuestions[index],
    ];

    setForm({
      ...form,
      questions: newQuestions,
    });
  };

  const handleQuestionChange = (index: number, field: keyof Question, value: any) => {
    const newQuestions = [...form.questions];
    newQuestions[index] = {
      ...newQuestions[index],
      [field]: value,
    };
    setForm({
      ...form,
      questions: newQuestions,
    });
  };

  const handleAddOption = (questionIndex: number) => {
    const question = form.questions[questionIndex];
    const newOptions = [...(question.options || []), { value: "", label: "" }];
    handleQuestionChange(questionIndex, "options", newOptions);
  };

  const handleOptionChange = (
    questionIndex: number,
    optionIndex: number,
    field: keyof Option,
    value: string
  ) => {
    const question = form.questions[questionIndex];
    const newOptions = [...(question.options || [])];
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      [field]: value,
    };
    handleQuestionChange(questionIndex, "options", newOptions);
  };

  const handleDeleteOption = (questionIndex: number, optionIndex: number) => {
    const question = form.questions[questionIndex];
    const newOptions = (question.options || []).filter((_, i) => i !== optionIndex);
    handleQuestionChange(questionIndex, "options", newOptions);
  };

  const handleAddConditionGroup = (questionIndex: number) => {
    const question = form.questions[questionIndex];
    const newConditions = [...(question.display_logic.conditions || []), []];
    handleQuestionChange(questionIndex, "display_logic", {
      ...question.display_logic,
      conditions: newConditions,
    });
  };

  const handleAddCondition = (questionIndex: number, groupIndex: number) => {
    const question = form.questions[questionIndex];
    const conditions = question.display_logic.conditions || [];
    const newGroup = [
      ...conditions[groupIndex],
      {
        source_question_id: "",
        operator: "equals" as const,
        value: "",
      },
    ];
    const newConditions = [...conditions];
    newConditions[groupIndex] = newGroup;
    handleQuestionChange(questionIndex, "display_logic", {
      ...question.display_logic,
      conditions: newConditions,
    });
  };

  const handleConditionChange = (
    questionIndex: number,
    groupIndex: number,
    conditionIndex: number,
    field: keyof Condition,
    value: any
  ) => {
    const question = form.questions[questionIndex];
    const conditions = question.display_logic.conditions || [];
    const newGroup = [...conditions[groupIndex]];
    newGroup[conditionIndex] = {
      ...newGroup[conditionIndex],
      [field]: value,
    };
    const newConditions = [...conditions];
    newConditions[groupIndex] = newGroup;
    handleQuestionChange(questionIndex, "display_logic", {
      ...question.display_logic,
      conditions: newConditions,
    });
  };

  const handleDeleteCondition = (
    questionIndex: number,
    groupIndex: number,
    conditionIndex: number
  ) => {
    const question = form.questions[questionIndex];
    const conditions = question.display_logic.conditions || [];
    const newGroup = conditions[groupIndex].filter((_, i) => i !== conditionIndex);
    const newConditions = [...conditions];
    if (newGroup.length === 0) {
      newConditions.splice(groupIndex, 1);
    } else {
      newConditions[groupIndex] = newGroup;
    }
    handleQuestionChange(questionIndex, "display_logic", {
      ...question.display_logic,
      conditions: newConditions.length > 0 ? newConditions : null,
    });
  };

  const handleImportJSON = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const importedForm = JSON.parse(event.target?.result as string) as Form;
            // Validate basic structure
            if (
              importedForm.form_id &&
              importedForm.title &&
              Array.isArray(importedForm.questions)
            ) {
              setForm(importedForm);
              setErrors({});
            } else {
              setErrors({ json: "El JSON no tiene la estructura correcta" });
            }
          } catch (error) {
            setErrors({ json: "Error al parsear el JSON: " + (error as Error).message });
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleExportJSON = () => {
    const jsonString = JSON.stringify(form, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `form-${form.form_id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePreviewSubmit = (answers: FormAnswers) => {
    setPreviewAnswers(answers);
  };

  const questionTypes: QuestionType[] = [
    "text",
    "textarea",
    "number",
    "select",
    "multiselect",
    "radio",
    "checkbox",
    "date",
    "datetime",
    "file",
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Constructor" icon={<Edit />} iconPosition="start" />
          <Tab label="Vista Previa" icon={<Preview />} iconPosition="start" />
          <Tab label="JSON" icon={<Code />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Form Metadata */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Información del Formulario</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Título"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    fullWidth
                    required
                    error={!!errors.title}
                    helperText={errors.title}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descripción"
                    value={form.description || ""}
                    onChange={(e) => setForm({ ...form, description: e.target.value || null })}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="ID del Formulario"
                    value={form.form_id}
                    onChange={(e) => setForm({ ...form, form_id: e.target.value })}
                    fullWidth
                    required
                    error={!!errors.form_id}
                    helperText={errors.form_id || "UUID único del formulario"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Conector (opcional)"
                    value={form.connector || ""}
                    onChange={(e) => setForm({ ...form, connector: e.target.value || null })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Questions */}
          <Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Typography variant="h6">Preguntas ({form.questions.length})</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={handleAddQuestion}>
                Agregar Pregunta
              </Button>
            </Box>

            {form.questions.length === 0 ? (
              <Alert severity="info">
                No hay preguntas. Agregue la primera pregunta para comenzar.
              </Alert>
            ) : (
              form.questions.map((question, index) => (
                <Accordion
                  key={question.id}
                  expanded={expandedQuestion === index}
                  onChange={(_, isExpanded) => setExpandedQuestion(isExpanded ? index : false)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        {question.text || `Pregunta ${index + 1}`}
                      </Typography>
                      <Chip label={question.type} size="small" />
                      {question.mandatory && <Chip label="Requerida" size="small" color="error" />}
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveQuestion(index, "up");
                          }}
                          disabled={index === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveQuestion(index, "down");
                          }}
                          disabled={index === form.questions.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteQuestion(index);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <QuestionEditor
                      question={question}
                      index={index}
                      allQuestions={form.questions}
                      onChange={handleQuestionChange}
                      onAddOption={handleAddOption}
                      onOptionChange={handleOptionChange}
                      onDeleteOption={handleDeleteOption}
                      onAddConditionGroup={handleAddConditionGroup}
                      onAddCondition={handleAddCondition}
                      onConditionChange={handleConditionChange}
                      onDeleteCondition={handleDeleteCondition}
                      errors={errors}
                      questionTypes={questionTypes}
                    />
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta es una vista previa del formulario. Puede completar los campos para probar el
            formulario.
          </Alert>
          {form.questions.length === 0 ? (
            <Alert severity="warning">
              Agregue al menos una pregunta para ver la vista previa.
            </Alert>
          ) : (
            <DynamicFormRenderer
              formDefinition={form}
              initialAnswers={null}
              onSubmit={handlePreviewSubmit}
              onCancel={() => {}}
            />
          )}
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button variant="outlined" startIcon={<Upload />} onClick={handleImportJSON}>
              Importar JSON
            </Button>
            <Button variant="outlined" startIcon={<Download />} onClick={handleExportJSON}>
              Exportar JSON
            </Button>
          </Box>
          {errors.json && <Alert severity="error">{errors.json}</Alert>}
          <TextField
            label="JSON del Formulario"
            value={JSON.stringify(form, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value) as Form;
                setForm(parsed);
                setErrors({});
              } catch (error) {
                // Invalid JSON, don't update
              }
            }}
            fullWidth
            multiline
            rows={20}
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>
      </TabPanel>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 3,
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {onCancel && (
          <Button variant="outlined" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button variant="contained" startIcon={<Save />} onClick={handleSave}>
          Guardar Formulario
        </Button>
      </Box>
    </Box>
  );
};

interface QuestionEditorProps {
  question: Question;
  index: number;
  allQuestions: Question[];
  onChange: (index: number, field: keyof Question, value: any) => void;
  onAddOption: (questionIndex: number) => void;
  onOptionChange: (
    questionIndex: number,
    optionIndex: number,
    field: keyof Option,
    value: string
  ) => void;
  onDeleteOption: (questionIndex: number, optionIndex: number) => void;
  onAddConditionGroup: (questionIndex: number) => void;
  onAddCondition: (questionIndex: number, groupIndex: number) => void;
  onConditionChange: (
    questionIndex: number,
    groupIndex: number,
    conditionIndex: number,
    field: keyof Condition,
    value: any
  ) => void;
  onDeleteCondition: (questionIndex: number, groupIndex: number, conditionIndex: number) => void;
  errors: Record<string, string>;
  questionTypes: QuestionType[];
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  index,
  allQuestions,
  onChange,
  onAddOption,
  onOptionChange,
  onDeleteOption,
  onAddConditionGroup,
  onAddCondition,
  onConditionChange,
  onDeleteCondition,
  errors,
  questionTypes,
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="ID de la Pregunta (stringId)"
            value={question.id}
            onChange={(e) => onChange(index, "id", e.target.value)}
            fullWidth
            required
            error={!!errors[`question_${index}_id`]}
            helperText={
              errors[`question_${index}_id`] || "Identificador de texto único de la pregunta"
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="UUID de la Pregunta"
            value={question.uuid || ""}
            onChange={(e) => onChange(index, "uuid", e.target.value || undefined)}
            fullWidth
            helperText="UUID del backend - NO modificar a menos que sea necesario para restaurar respuestas existentes"
            placeholder="Se genera automáticamente si se deja vacío"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto de la Pregunta"
            value={question.text}
            onChange={(e) => onChange(index, "text", e.target.value)}
            fullWidth
            required
            error={!!errors[`question_${index}_text`]}
            helperText={errors[`question_${index}_text`]}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto de Ayuda"
            value={question.help_text || ""}
            onChange={(e) => onChange(index, "help_text", e.target.value || null)}
            fullWidth
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Pregunta</InputLabel>
            <Select
              value={question.type}
              onChange={(e) => onChange(index, "type", e.target.value as QuestionType)}
              label="Tipo de Pregunta"
            >
              {questionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={question.mandatory}
                onChange={(e) => onChange(index, "mandatory", e.target.checked)}
              />
            }
            label="Pregunta Requerida"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Placeholder"
            value={question.placeholder || ""}
            onChange={(e) => onChange(index, "placeholder", e.target.value || null)}
            fullWidth
          />
        </Grid>
        {question.type !== "checkbox" && question.type !== "file" && (
          <Grid item xs={12}>
            <TextField
              label="Valor por Defecto"
              value={question.default_value || ""}
              onChange={(e) => onChange(index, "default_value", e.target.value || null)}
              fullWidth
            />
          </Grid>
        )}

        {/* Options Editor for select/multiselect/radio */}
        {["select", "multiselect", "radio"].includes(question.type) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1">Opciones</Typography>
                <Button size="small" startIcon={<Add />} onClick={() => onAddOption(index)}>
                  Agregar Opción
                </Button>
              </Box>
              {errors[`question_${index}_options`] && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors[`question_${index}_options`]}
                </Alert>
              )}
              {(question.options || []).map((option, optIndex) => (
                <Box key={optIndex} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "4px",
                      border: "1px solid rgba(0,0,0,0.23)",
                      cursor: "pointer",
                      backgroundColor: option.color || "#ffffff",
                      flexShrink: 0,
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                    onClick={() => {
                      const input = document.getElementById(
                        `color-picker-${index}-${optIndex}`
                      ) as HTMLInputElement;
                      input?.click();
                    }}
                  >
                    <input
                      type="color"
                      id={`color-picker-${index}-${optIndex}`}
                      value={option.color || "#ffffff"}
                      onChange={(e) => onOptionChange(index, optIndex, "color", e.target.value)}
                      style={{
                        position: "absolute",
                        top: -4,
                        left: -4,
                        width: 44,
                        height: 44,
                        border: "none",
                        cursor: "pointer",
                        opacity: 0,
                      }}
                    />
                  </Box>
                  <TextField
                    label="Valor"
                    value={option.value}
                    onChange={(e) => onOptionChange(index, optIndex, "value", e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Etiqueta"
                    value={option.label}
                    onChange={(e) => onOptionChange(index, optIndex, "label", e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteOption(index, optIndex)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>
          </Grid>
        )}

        {/* Validation Editor */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Validación
            </Typography>
            <Grid container spacing={2}>
              {(question.type === "text" || question.type === "textarea") && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      label="Longitud Mínima"
                      type="number"
                      value={question.validation?.min_length || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          min_length: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Longitud Máxima"
                      type="number"
                      value={question.validation?.max_length || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          max_length: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Patrón (Regex)"
                      value={question.validation?.pattern || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          pattern: e.target.value || null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              {question.type === "number" && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      label="Valor Mínimo"
                      type="number"
                      value={question.validation?.min_value || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          min_value: e.target.value ? parseFloat(e.target.value) : null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Valor Máximo"
                      type="number"
                      value={question.validation?.max_value || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          max_value: e.target.value ? parseFloat(e.target.value) : null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              {question.type === "multiselect" && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      label="Selecciones Mínimas"
                      type="number"
                      value={question.validation?.min_selections || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          min_selections: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Selecciones Máximas"
                      type="number"
                      value={question.validation?.max_selections || ""}
                      onChange={(e) =>
                        onChange(index, "validation", {
                          ...question.validation,
                          max_selections: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Display Logic Editor */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Lógica de Visualización
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Condición</InputLabel>
              <Select
                value={question.display_logic.condition_type}
                onChange={(e) =>
                  onChange(index, "display_logic", {
                    ...question.display_logic,
                    condition_type: e.target.value as "always" | "if" | "for_each",
                    conditions:
                      e.target.value === "always" ? null : question.display_logic.conditions,
                    source_multiselect_id:
                      e.target.value !== "for_each"
                        ? null
                        : question.display_logic.source_multiselect_id,
                    context_label_template:
                      e.target.value !== "for_each"
                        ? null
                        : question.display_logic.context_label_template,
                  })
                }
                label="Tipo de Condición"
              >
                <MenuItem value="always">Siempre</MenuItem>
                <MenuItem value="if">Si (condiciones)</MenuItem>
                <MenuItem value="for_each">Para cada (repetición)</MenuItem>
              </Select>
            </FormControl>

            {question.display_logic.condition_type === "if" && (
              <Box>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => onAddConditionGroup(index)}
                  sx={{ mb: 2 }}
                >
                  Agregar Grupo de Condiciones (OR)
                </Button>
                {(question.display_logic.conditions || []).map((group, groupIndex) => (
                  <Paper key={groupIndex} sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        Grupo {groupIndex + 1} (AND)
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const conditions = question.display_logic.conditions || [];
                          const newConditions = conditions.filter((_, i) => i !== groupIndex);
                          onChange(index, "display_logic", {
                            ...question.display_logic,
                            conditions: newConditions.length > 0 ? newConditions : null,
                          });
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    {group.map((condition, condIndex) => (
                      <Box
                        key={condIndex}
                        sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}
                      >
                        <FormControl sx={{ minWidth: 200 }}>
                          <InputLabel>Pregunta Fuente</InputLabel>
                          <Select
                            value={condition.source_question_id}
                            onChange={(e) =>
                              onConditionChange(
                                index,
                                groupIndex,
                                condIndex,
                                "source_question_id",
                                e.target.value
                              )
                            }
                            label="Pregunta Fuente"
                          >
                            {allQuestions
                              .filter((q) => q.id !== question.id)
                              .map((q) => (
                                <MenuItem key={q.id} value={q.id}>
                                  {q.text || q.id}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 150 }}>
                          <InputLabel>Operador</InputLabel>
                          <Select
                            value={condition.operator}
                            onChange={(e) =>
                              onConditionChange(
                                index,
                                groupIndex,
                                condIndex,
                                "operator",
                                e.target.value
                              )
                            }
                            label="Operador"
                          >
                            <MenuItem value="equals">Igual a</MenuItem>
                            <MenuItem value="not_equals">No igual a</MenuItem>
                            <MenuItem value="greater_than">Mayor que</MenuItem>
                            <MenuItem value="less_than">Menor que</MenuItem>
                            <MenuItem value="contains">Contiene</MenuItem>
                            <MenuItem value="does_not_contain">No contiene</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Valor"
                          value={condition.value}
                          onChange={(e) =>
                            onConditionChange(index, groupIndex, condIndex, "value", e.target.value)
                          }
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => onDeleteCondition(index, groupIndex, condIndex)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => onAddCondition(index, groupIndex)}
                    >
                      Agregar Condición (AND)
                    </Button>
                  </Paper>
                ))}
              </Box>
            )}

            {question.display_logic.condition_type === "for_each" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Pregunta Multiselect Fuente</InputLabel>
                  <Select
                    value={question.display_logic.source_multiselect_id || ""}
                    onChange={(e) =>
                      onChange(index, "display_logic", {
                        ...question.display_logic,
                        source_multiselect_id: e.target.value || null,
                      })
                    }
                    label="Pregunta Multiselect Fuente"
                    error={!!errors[`question_${index}_for_each_source`]}
                  >
                    {allQuestions
                      .filter((q) => q.type === "multiselect" && q.id !== question.id)
                      .map((q) => (
                        <MenuItem key={q.id} value={q.id}>
                          {q.text || q.id}
                        </MenuItem>
                      ))}
                  </Select>
                  {errors[`question_${index}_for_each_source`] && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors[`question_${index}_for_each_source`]}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  label="Plantilla de Etiqueta de Contexto"
                  value={question.display_logic.context_label_template || ""}
                  onChange={(e) =>
                    onChange(index, "display_logic", {
                      ...question.display_logic,
                      context_label_template: e.target.value || null,
                    })
                  }
                  fullWidth
                  placeholder="Ej: Detalles para {label}"
                  helperText="Use {label} para insertar la etiqueta de la opción seleccionada"
                />
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FormCreator;
